// Cliente de las 9 rutas del contrato de captura (ADR 0004 §6) con peticiones firmadas (§5).
import { randomHex, sha256Hex, utf8 } from './crypto.js';
import { requestMessage, type CaptureSigner } from './SessionKeys.js';
import type {
  CaptureBundle,
  CaptureConfig,
  CaptureHandoff,
  CaptureHandoffCreate,
  CaptureProgress,
  CaptureResult,
  CaptureSession,
  CaptureSessionCreate,
  CaptureSessionStatus,
  CaptureTelemetry,
  CaptureUploadReceipt,
} from './types.js';

export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

/** Error HTTP de la API (problem+json si lo hay). */
export class CaptureApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly problem?: unknown,
  ) {
    super(`capture_api_${status}_${code}`);
    this.name = 'CaptureApiError';
  }

  /** ¿Merece la pena reintentar? (red, 408, 429 y 5xx). */
  get retryable(): boolean {
    return this.status === 0 || this.status === 408 || this.status === 429 || this.status >= 500;
  }
}

export interface CaptureClientOptions {
  /** Origen de la API, p. ej. `https://api.knowyourparcel.com` (sin barra final). */
  apiBase: string;
  fetch?: FetchLike;
  /** ms Unix para `X-KYP-Timestamp` (por defecto `Date.now`). */
  now?: () => number;
}

/** Sesión a la que se atan las peticiones firmadas. */
interface Binding {
  sessionId: string;
  signer: CaptureSigner;
}

/**
 * Cliente tipado del contrato de captura. Las rutas `signed` llevan `X-KYP-Session`,
 * `X-KYP-Nonce`, `X-KYP-Timestamp` y `X-KYP-Signature` (ADR 0004 §5) una vez llamada `bind()`.
 */
export class CaptureClient {
  readonly apiBase: string;
  private readonly fetchFn: FetchLike;
  private readonly now: () => number;
  private binding: Binding | null = null;

  constructor(opts: CaptureClientOptions) {
    this.apiBase = opts.apiBase.replace(/\/+$/, '');
    this.fetchFn = opts.fetch ?? ((input, init) => globalThis.fetch(input, init));
    this.now = opts.now ?? Date.now;
  }

  /** Ata el cliente a una sesión: a partir de aquí las rutas firmadas se pueden llamar. */
  bind(sessionId: string, signer: CaptureSigner): void {
    this.binding = { sessionId, signer };
  }

  /** `GET /v1/capture/config`. */
  getConfig(): Promise<CaptureConfig> {
    return this.json('GET', '/v1/capture/config');
  }

  /** `POST /v1/capture/handoffs` (escritorio → móvil). */
  createHandoff(body: CaptureHandoffCreate): Promise<CaptureHandoff> {
    return this.json('POST', '/v1/capture/handoffs', body);
  }

  /** URL de `GET /v1/capture/handoffs/{id}/events?token=` (Server-Sent Events). */
  handoffEventsUrl(id: string, token: string): string {
    return `${this.apiBase}/v1/capture/handoffs/${encodeURIComponent(id)}/events?token=${encodeURIComponent(token)}`;
  }

  /** `POST /v1/capture/sessions`. */
  createSession(body: CaptureSessionCreate): Promise<CaptureSession> {
    return this.json('POST', '/v1/capture/sessions', body);
  }

  /** `GET /v1/capture/sessions/{id}` (firmada). */
  async getSession(): Promise<CaptureSessionStatus> {
    return this.signedJson('GET', this.sessionPath(''));
  }

  /** `POST /v1/capture/sessions/{id}/frames` (firmada, binario con `X-KYP-Sha256`). */
  async uploadBinary(bytes: Uint8Array, sha256?: string): Promise<CaptureUploadReceipt> {
    const hash = sha256 ?? (await sha256Hex(bytes));
    return this.signedJson('POST', this.sessionPath('/frames'), bytes, {
      'content-type': 'application/octet-stream',
      'x-kyp-sha256': hash,
    });
  }

  /** `POST /v1/capture/sessions/{id}/progress` (firmada). */
  async reportProgress(body: CaptureProgress): Promise<void> {
    await this.signedJson('POST', this.sessionPath('/progress'), jsonBytes(body), JSON_HEADERS);
  }

  /** `POST /v1/capture/sessions/{id}/complete` (firmada) con el bundle firmado. */
  async complete(bundle: CaptureBundle): Promise<CaptureResult> {
    return this.signedJson('POST', this.sessionPath('/complete'), jsonBytes(bundle), JSON_HEADERS);
  }

  /** `POST /v1/capture/telemetry`. */
  async telemetry(body: CaptureTelemetry): Promise<void> {
    await this.json('POST', '/v1/capture/telemetry', body);
  }

  private sessionPath(suffix: string): string {
    if (!this.binding) throw new Error('capture_session_not_bound');
    return `/v1/capture/sessions/${encodeURIComponent(this.binding.sessionId)}${suffix}`;
  }

  private json<T>(method: string, path: string, body?: unknown): Promise<T> {
    return this.send<T>(
      method,
      path,
      body === undefined ? undefined : jsonBytes(body),
      body === undefined ? {} : JSON_HEADERS,
    );
  }

  private async signedJson<T>(
    method: string,
    path: string,
    body?: Uint8Array,
    headers: Record<string, string> = {},
  ): Promise<T> {
    const b = this.binding;
    if (!b) throw new Error('capture_session_not_bound');
    const url = new URL(this.apiBase + path);
    const nonce = randomHex(16);
    const timestamp = Math.round(this.now());
    const signature = await b.signer.sign(
      requestMessage({
        session_id: b.sessionId,
        nonce,
        timestamp,
        method,
        // Se firma la ruta tal y como la recibe el servidor (incluido el prefijo de `apiBase`).
        path: url.pathname,
        body_sha256: await sha256Hex(body ?? new Uint8Array(0)),
      }),
    );
    return this.send<T>(method, path, body, {
      ...headers,
      'x-kyp-session': b.sessionId,
      'x-kyp-nonce': nonce,
      'x-kyp-timestamp': String(timestamp),
      'x-kyp-signature': signature,
    });
  }

  private async send<T>(
    method: string,
    path: string,
    body: Uint8Array | undefined,
    headers: Record<string, string>,
  ): Promise<T> {
    let res: Response;
    try {
      res = await this.fetchFn(this.apiBase + path, {
        method,
        headers,
        body: body === undefined ? undefined : new Uint8Array(body),
      });
    } catch (err) {
      throw new CaptureApiError(0, 'network_error', String(err));
    }
    const text = await res.text();
    const data: unknown = text ? safeJson(text) : undefined;
    if (!res.ok) {
      const code =
        data && typeof data === 'object' && 'code' in data && typeof data.code === 'string'
          ? data.code
          : 'http_error';
      throw new CaptureApiError(res.status, code, data);
    }
    return data as T;
  }
}

const JSON_HEADERS = { 'content-type': 'application/json' };

function jsonBytes(body: unknown): Uint8Array {
  return utf8(JSON.stringify(body));
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}
